import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useUserAuth } from '../context/userAuthContext';
import { 
  MapPin, 
  Search, 
  Bell, 
  ChevronDown, 
  Sliders, 
  Calendar,
  Heart,
  Plus
} from 'lucide-react';

const Feed = () => {
  const navigate = useNavigate();
  const { user } = useUserAuth();
  
  // Estados
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [likedEvents, setLikedEvents] = useState(new Set());
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [locationDropdown, setLocationDropdown] = useState(false);
  const [notifications, setNotifications] = useState(2);

  // Cargar categorías desde Firestore
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "categories"));
        const categoriesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCategories(categoriesData);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  // Cargar eventos desde Firestore
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const q = query(
          collection(db, "activities"),
          orderBy("createdAt", "desc")
        );
        
        const querySnapshot = await getDocs(q);
        const eventsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setAllEvents(eventsData);
        setFilteredEvents(eventsData);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Filtrar eventos
  useEffect(() => {
    let filtered = [...allEvents];
    
    if (searchTerm) {
      filtered = filtered.filter(event => 
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(event => 
        event.category === selectedCategory
      );
    }

    setFilteredEvents(filtered);
  }, [searchTerm, selectedCategory, allEvents]);

  const formatDateTime = (dateTime) => {
    const date = new Date(dateTime);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleLike = (eventId) => {
    setLikedEvents(prev => {
      const newLiked = new Set(prev);
      if (newLiked.has(eventId)) {
        newLiked.delete(eventId);
      } else {
        newLiked.add(eventId);
      }
      return newLiked;
    });
  };

  const handleCategoryClick = (categoryName) => {
    setSelectedCategory(prev => prev === categoryName ? null : categoryName);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory(null);
  };

  const visibleCategories = showAllCategories ? categories : categories.slice(0, 4);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-gray-50 min-h-screen pb-20">
      {/* Header */}
      <div className="p-4 space-y-4">
        {/* Location Dropdown */}
        <div className="flex justify-between items-center">
          <div className="relative">
            <button 
              className="flex items-center space-x-2"
              onClick={() => setLocationDropdown(!locationDropdown)}
            >
              <MapPin className="w-6 h-6 text-orange-500" />
              <div>
                <h2 className="text-sm text-gray-500">Location</h2>
                <div className="flex items-center">
                  <span className="font-semibold">Mexico City, MX</span>
                  <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${locationDropdown ? 'rotate-180' : ''}`} />
                </div>
              </div>
            </button>
            {locationDropdown && (
              <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-lg py-2 z-10">
                <button className="w-full px-4 py-2 text-left hover:bg-gray-50">Mexico City, MX</button>
                <button className="w-full px-4 py-2 text-left hover:bg-gray-50">Guadalajara, MX</button>
                <button className="w-full px-4 py-2 text-left hover:bg-gray-50">Monterrey, MX</button>
              </div>
            )}
          </div>
          <button 
            className="relative"
            onClick={() => setNotifications(0)}
          >
            <Bell className="w-6 h-6 text-gray-700" />
            {notifications > 0 && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            )}
          </button>
        </div>

        {/* Search Bar */}
        <div className="flex gap-2">
          <div className="flex-1 bg-white rounded-full border border-gray-200 px-4 py-2.5 flex items-center">
            <Search className="w-5 h-5 text-gray-400 mr-2" />
            <input 
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search Events, Organizer"
              className="w-full bg-transparent outline-none text-sm"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            )}
          </div>
          <button 
            className={`p-3 rounded-xl transition-colors ${selectedCategory ? 'bg-orange-500' : 'bg-orange-400'}`}
            onClick={clearFilters}
          >
            <Sliders className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Categories */}
      <div className="px-4 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Categories</h2>
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/newcategory')}
              className="text-orange-400 hover:text-orange-500"
            >
              <Plus className="w-5 h-5" />
            </button>
            <button 
              className="text-orange-400 text-sm"
              onClick={() => setShowAllCategories(!showAllCategories)}
            >
              {showAllCategories ? 'Show Less' : 'See all'}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4">
          {visibleCategories.map(category => (
            <button
              key={category.id}
              onClick={() => handleCategoryClick(category.categoryName)}
              className="flex flex-col items-center focus:outline-none"
            >
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-2 transition-colors ${
                selectedCategory === category.categoryName 
                  ? 'bg-orange-400' 
                  : 'bg-orange-50'
              }`}>
                <img 
                  src={category.categoryIcon} 
                  alt={category.categoryName}
                  className={`w-8 h-8 object-contain ${
                    selectedCategory === category.categoryName 
                      ? 'brightness-0 invert' 
                      : ''
                  }`}
                />
              </div>
              <span className="text-sm truncate w-full text-center">
                {category.categoryName}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Upcoming Events */}
      <div className="px-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Upcoming Events</h2>
          <button 
            onClick={() => navigate('/myactivities')} 
            className="text-orange-400 text-sm"
          >
            See all
          </button>
        </div>
        <div className="space-y-4">
          {filteredEvents.map(event => (
            <div 
              key={event.id} 
              className="bg-white rounded-3xl overflow-hidden cursor-pointer"
              onClick={() => navigate(`/go/${event.id}`)}
            >
              <div className="relative">
                <img 
                  src={event.coverImage}
                  alt={event.title}
                  className="w-full h-48 object-cover"
                />
                <button 
                  className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center transition-transform hover:scale-110"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleLike(event.id);
                  }}
                >
                  <Heart 
                    className={`w-5 h-5 transition-colors ${
                      likedEvents.has(event.id) 
                        ? 'text-red-500 fill-red-500' 
                        : 'text-gray-600'
                    }`} 
                  />
                </button>
                <span className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm">
                  {event.category || 'Social'}
                </span>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-lg mb-2">{event.title}</h3>
                <div className="space-y-2 mb-3">
                  <div className="flex items-center text-gray-600">
                    <MapPin className="w-4 h-4 mr-2" />
                    <span className="text-sm">{event.location}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span className="text-sm">{formatDateTime(event.eventDateTime)}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center -space-x-2">
                    {[...Array(Math.min(4, event.attendees?.length || 0))].map((_, i) => (
                      <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-gray-200"></div>
                    ))}
                    <div className="w-8 h-8 rounded-full bg-orange-400 flex items-center justify-center text-white text-xs ml-1">
                      {event.attendees?.length || 0}
                    </div>
                  </div>
                  <span className="text-sm font-medium">
                    {event.attendees?.length || 0} / {event.maxAttendees} attendees
                  </span>
                </div>
              </div>
            </div>
          ))}

          {filteredEvents.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">No events found matching your search.</p>
              <button 
                onClick={clearFilters}
                className="mt-2 text-orange-400 hover:text-orange-500"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Feed;